/**
 * useUpdateOrgMemberProfile — TanStack mutation over
 * `PATCH /organizations/:slug/users/:userId`.
 *
 * Threads `If-Match: "<version>"` from the caller's last-known ETag
 * (captured by `useOrgMemberDetail` — the bare-integer strong tag the BE
 * actually expects; the controller docstring's `"v5"` example contradicts
 * the implementation, see `apps/api/.../utils/parse-if-match.ts`) and
 * maps the BE's error envelope to a tagged
 * union so consumers can branch without sniffing HTTP status codes
 * themselves.
 *
 * Error union:
 *   - 'stale'        — 412 Precondition Failed; carries `staleVersion`
 *                       from the BE body so the form can hint the user
 *                       at the current server version.
 *   - 'unprocessable' — 422; carries `message` (e.g. SelfManagerError,
 *                       UnknownManagerError) so the form can map to
 *                       field-level errors.
 *   - 'forbidden'    — 403.
 *   - 'notFound'     — 404.
 *   - 'error'        — anything else.
 *
 * Cache discipline: on both success AND 412 the detail query is
 * invalidated. The 412 invalidation is load-bearing — otherwise the
 * stale ETag is reused on the next save attempt and the user gets
 * stuck in an infinite 412 loop.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { paths } from '@artemis/contracts';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { buildDetailQueryKey } from './use-org-member-detail.js';
import type { OrgMemberDetailView } from './types.js';

type PatchPath =
  paths['/organizations/{slug}/users/{userId}']['patch'];
type PatchRequestBody = NonNullable<
  PatchPath['requestBody']
>['content']['application/json'];

export interface UpdateOrgMemberProfileInput {
  readonly userId: string;
  /** Strong ETag from the most recent GET — `"<version>"`. */
  readonly etag: string;
  readonly body: PatchRequestBody;
}

export type UpdateProfileError =
  | { readonly kind: 'stale'; readonly staleVersion: number }
  | { readonly kind: 'unprocessable'; readonly message: string }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'notFound' }
  | { readonly kind: 'error' };

interface DetailDto {
  readonly id: string;
  readonly email: string;
  readonly firstName?: string | null;
  readonly lastName?: string | null;
  readonly emailVerifiedAt?: string | null;
  readonly status: 'active' | 'pending_verification' | 'suspended';
  readonly roles: ReadonlyArray<{ slug: string; displayName: string }>;
  readonly joinedAt: string;
  readonly lastSeenAt?: string | null;
  readonly jobTitle?: string | null;
  readonly department?: string | null;
  readonly phone?: string | null;
  readonly timeZone: string;
  readonly language: string;
  readonly country?: string | null;
  readonly managerId?: string | null;
  readonly version: number;
}

function projectDetail(dto: DetailDto): OrgMemberDetailView {
  return {
    userId: dto.id,
    email: dto.email,
    firstName: dto.firstName ?? null,
    lastName: dto.lastName ?? null,
    emailVerifiedAt: dto.emailVerifiedAt ?? null,
    status: dto.status,
    roles: dto.roles,
    joinedAt: dto.joinedAt,
    lastSeenAt: dto.lastSeenAt ?? null,
    jobTitle: dto.jobTitle ?? null,
    department: dto.department ?? null,
    phone: dto.phone ?? null,
    timeZone: dto.timeZone,
    language: dto.language,
    country: dto.country ?? null,
    managerId: dto.managerId ?? null,
    version: dto.version,
  };
}

export interface UseUpdateOrgMemberProfileResult {
  readonly mutateAsync: (
    input: UpdateOrgMemberProfileInput,
  ) => Promise<OrgMemberDetailView>;
  readonly isPending: boolean;
}

export function useUpdateOrgMemberProfile(): UseUpdateOrgMemberProfileResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;
  const queryClient = useQueryClient();

  const mutation = useMutation<
    OrgMemberDetailView,
    UpdateProfileError,
    UpdateOrgMemberProfileInput
  >({
    mutationFn: async (input): Promise<OrgMemberDetailView> => {
      if (orgSlug === null) {
        throw { kind: 'error' } satisfies UpdateProfileError;
      }
      const result = await apiClient.PATCH(
        '/organizations/{slug}/users/{userId}',
        {
          params: {
            // `If-Match` is a typed header PARAMETER on this operation (the
            // contract declares `parameters.header['If-Match']` as required),
            // not a free-form request header. Pass it through `params.header`
            // like every other If-Match mutation in the app
            // (use-ownership-mutations, use-update-property-status) so the
            // typed client threads it into the request.
            header: { 'If-Match': input.etag },
            path: { slug: orgSlug, userId: input.userId },
          },
          body: input.body,
        },
      );
      const { response, data, error } = result;
      if (response.status === 412) {
        const body = (error ?? data) as { currentVersion?: unknown } | undefined;
        const staleVersion =
          typeof body?.currentVersion === 'number' ? body.currentVersion : -1;
        await queryClient.invalidateQueries({
          queryKey: buildDetailQueryKey(orgSlug, input.userId),
        });
        throw {
          kind: 'stale',
          staleVersion,
        } satisfies UpdateProfileError;
      }
      if (response.status === 422) {
        const body = (error ?? data) as { message?: unknown } | undefined;
        const message =
          typeof body?.message === 'string' ? body.message : 'Unprocessable';
        throw {
          kind: 'unprocessable',
          message,
        } satisfies UpdateProfileError;
      }
      if (response.status === 403) {
        throw { kind: 'forbidden' } satisfies UpdateProfileError;
      }
      if (response.status === 404) {
        throw { kind: 'notFound' } satisfies UpdateProfileError;
      }
      if (!response.ok || data === undefined) {
        throw { kind: 'error' } satisfies UpdateProfileError;
      }
      return projectDetail(data as DetailDto);
    },
    onSuccess: async (_data, input) => {
      await queryClient.invalidateQueries({
        queryKey: buildDetailQueryKey(orgSlug, input.userId),
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
